import { normalizeCounty, type AddressValue } from "@/lib/address";
import type { IntakeProposal } from "@/lib/brokerDocuments";
import { WC_ENTITY_TYPE_OPTIONS } from "@/lib/submissions/wc/business";
import { US_STATES } from "@/lib/usStates";
import type {
  CityLimits,
  PremisesInterest,
} from "@/lib/submissions/brokerSubmissions";
import {
  formatEin,
  insuredEditable,
  type WcBusiness,
} from "@/lib/submissions/wc/business";
import type { WcDraft } from "@/lib/submissions/wc/draft";
import {
  buildingHasContent,
  EMPTY_BUILDING,
  isBlankLocation,
  newClassRow,
  newLocation,
  sameAddress,
  type WcBuilding,
  type WcClassRow,
  type WcLocation,
  type WcMailing,
} from "@/lib/submissions/wc/locations";
import type { FieldSection } from "@/lib/brokerProducts";
import { answersFrom } from "@/lib/submissions/wc/questions";

// What an uploaded ACORD packet fills in the Workers' Comp wizard (BRK-S306).
// PMS classifies and reads the documents (PMS-S131) and hands back a proposal;
// `wcFromProposal` only places what it read in the wizard's own shapes. Each
// step's prefill lays its part on the working copy when the step opens, and
// fills only what is empty: a document never replaces what PMS holds or what
// the broker typed. The effective date is never filled (a fresh one is always
// entered), and neither are the people: extraction can't yet say who is an
// owner and who a contact on this submission's own list (PMS-S740).

/** The Business step's fields a document can fill. */
export type WcDocumentBusiness = Partial<
  Pick<
    WcBusiness,
    | "legalName"
    | "dbaName"
    | "state"
    | "ein"
    | "entityType"
    | "emailAddress"
    | "phone"
  >
>;

/** What a document says, in the wizard's shapes. */
export interface WcDocument {
  business: WcDocumentBusiness;
  /**
   * The locations the Locations step starts from: the premises, else the
   * mailing address. Every class-code row sits on location 1, because
   * extraction names a row's location only as text (`locationDescription`);
   * with more than one location, each row asks the broker to check it. Empty
   * when the document names neither an address nor a row.
   */
  locations: WcLocation[];
  /** The insured's mailing address, when the document gives one. */
  mailing: AddressValue | null;
  /** The document's Workers' Comp values, keyed as the manifest keys them:
   *  read against the questions once they load. */
  wc: Record<string, unknown>;
}

/** A step's working copy with a document's values laid on, and the keys of
 *  the fields they filled, which the step marks for the broker to confirm. */
export interface WcPrefilled {
  draft: WcDraft;
  keys: string[];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** A trimmed non-empty string, or null. */
function str(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

/** A USPS code from a code or a state's full name, or null. */
function stateCode(value: unknown): string | null {
  const raw = str(value)?.toLowerCase();
  if (!raw) return null;
  const state = US_STATES.find(
    (s) => s.code.toLowerCase() === raw || s.name.toLowerCase() === raw,
  );
  return state?.code ?? null;
}

/** A whole number of 0 or more, else undefined. */
function count(value: unknown): number | undefined {
  return typeof value === "number" && Number.isInteger(value) && value >= 0
    ? value
    : undefined;
}

/** An amount of 0 or more, else undefined. */
function amount(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) && value >= 0
    ? value
    : undefined;
}

/** The occupied area as a number of square feet: extraction gives it as
 *  printed ("12,500 sq ft"). */
function area(value: unknown): number | undefined {
  if (typeof value === "number") return amount(value);
  const digits = str(value)?.match(/\d[\d,]*(\.\d+)?/)?.[0];
  return digits ? amount(Number(digits.replace(/,/g, ""))) : undefined;
}

/** An extracted address, or null when it names nothing. A state the list
 *  doesn't have reads blank, so the broker chooses it; the county is bare, as
 *  PMS stores it (BRK-S308). */
function addressFrom(value: unknown): AddressValue | null {
  if (!isRecord(value)) return null;
  const street = str(value.street);
  const city = str(value.city);
  const state = stateCode(value.state);
  const zip = str(value.zip);
  if (!street && !city && !state && !zip) return null;
  const addressLine2 = str(value.addressLine2);
  const county = str(value.county);
  return {
    street: street ?? "",
    ...(addressLine2 ? { addressLine2 } : {}),
    city: city ?? "",
    state: state ?? "",
    zip: zip ?? "",
    ...(county ? { county: normalizeCounty(county) } : {}),
  };
}

const INTERESTS: readonly PremisesInterest[] = ["owner", "tenant"];
const CITY_LIMITS: readonly CityLimits[] = ["inside", "outside"];

function buildingFrom(premises: Record<string, unknown>): WcBuilding {
  return {
    ...EMPTY_BUILDING,
    bldNumber: str(premises.bldNumber) ?? "",
    yearBuilt: count(premises.yearBuilt),
    occupiedArea: area(premises.occupiedArea),
    interest: INTERESTS.find((i) => i === premises.interest) ?? "",
    cityLimits: CITY_LIMITS.find((c) => c === premises.cityLimits) ?? "",
  };
}

/** A premises as a location, its building details open when it has any. */
function locationFrom(premises: unknown, key: string): WcLocation | null {
  const address = addressFrom(premises);
  if (!address || !isRecord(premises)) return null;
  const building = buildingFrom(premises);
  const locNumber = str(premises.locNumber);
  return {
    ...newLocation(key),
    ...(locNumber ? { locNumber } : {}),
    address,
    hasBuildingDetails: buildingHasContent(building) ? true : null,
    building,
  };
}

/** An exposure as a class-code row: a code PMS matched as its chip, one it
 *  couldn't as the code the document printed. Null when it names nothing. */
function rowFrom(
  exposure: unknown,
  key: string,
  checkLocation: boolean,
): WcClassRow | null {
  if (!isRecord(exposure)) return null;
  const printed = str(exposure.classCode);
  const codeId = str(exposure.codeId);
  const fullTime = count(exposure.fullTimeCount);
  const partTime = count(exposure.partTimeCount);
  const payroll = amount(exposure.payroll);
  if (
    !printed &&
    !codeId &&
    fullTime === undefined &&
    partTime === undefined &&
    payroll === undefined
  ) {
    return null;
  }
  return {
    ...newClassRow(key),
    code: codeId
      ? {
          id: codeId,
          code: printed ?? codeId,
          typeName: "",
          friendlyName: null,
          description: null,
        }
      : null,
    ...(!codeId && printed ? { documentCode: printed } : {}),
    fullTime,
    partTime,
    payroll,
    ...(checkLocation ? { checkLocation: "document" as const } : {}),
  };
}

/** The first phone number, whichever spelling extraction used. */
function phoneFrom(account: Record<string, unknown>): string | null {
  const phones: unknown[] = Array.isArray(account.phones)
    ? account.phones
    : [account.primaryPhone];
  for (const phone of phones) {
    const number = isRecord(phone) ? str(phone.number) : str(phone);
    if (number) return number;
  }
  return null;
}

function businessFrom(account: Record<string, unknown>): WcDocumentBusiness {
  const business: WcDocumentBusiness = {};
  const legalName = str(account.legalName);
  if (legalName) business.legalName = legalName;
  const dbaName = str(account.dbaName);
  if (dbaName) business.dbaName = dbaName;
  const state =
    addressFrom(account.mailingAddress)?.state ||
    addressFrom(account.legalAddress)?.state ||
    stateCode(account.state);
  if (state) business.state = state;
  const ein = str(account.ein);
  if (ein) business.ein = formatEin(ein);
  // Only an entity Workers' Comp offers (BRK-S388): a document's joint
  // venture stays the broker's to choose.
  const entityType = WC_ENTITY_TYPE_OPTIONS.find(
    (o) => o.value === account.entityType,
  )?.value;
  if (entityType) business.entityType = entityType;
  const emailAddress = str(account.emailAddress);
  if (emailAddress) business.emailAddress = emailAddress;
  const phone = phoneFrom(account);
  if (phone) business.phone = phone;
  return business;
}

/** What a document says, in the wizard's shapes (BRK-S306). */
export function wcFromProposal(proposal: IntakeProposal): WcDocument {
  const account = isRecord(proposal.proposed.account)
    ? proposal.proposed.account
    : {};
  const wc = isRecord(proposal.proposed.wc) ? proposal.proposed.wc : {};
  const mailing =
    addressFrom(account.mailingAddress) ?? addressFrom(account.legalAddress);

  const premises: unknown[] = Array.isArray(account.physicalAddresses)
    ? account.physicalAddresses
    : [];
  let locations: WcLocation[] = [];
  for (const p of premises) {
    const location = locationFrom(
      p,
      `document-location-${locations.length + 1}`,
    );
    if (location) locations.push(location);
  }
  if (locations.length === 0 && mailing) {
    locations = [{ ...newLocation("document-location-1"), address: mailing }];
  }

  const exposures: unknown[] = Array.isArray(wc.exposures) ? wc.exposures : [];
  const rows: WcClassRow[] = [];
  for (const e of exposures) {
    const row = rowFrom(
      e,
      `document-row-${rows.length + 1}`,
      locations.length > 1,
    );
    if (row) rows.push(row);
  }
  if (rows.length > 0) {
    const [first = newLocation("document-location-1"), ...rest] = locations;
    locations = [{ ...first, rows }, ...rest];
  }

  return { business: businessFrom(account), locations, mailing, wc };
}

/** The Business step's text fields a document can fill. */
const BUSINESS_TEXT = [
  "legalName",
  "dbaName",
  "state",
  "ein",
  "emailAddress",
  "phone",
] as const;

/**
 * The Business step's empty fields, filled from the document. Nothing while the
 * insured is on record: its details change only by endorsement (Nav,
 * 2026-10-08). Nor the state once the application has started, since it then
 * follows the mailing address on the Locations step.
 */
export function prefillBusiness(draft: WcDraft, doc: WcDocument): WcPrefilled {
  if (!insuredEditable(draft.account, draft.submission)) {
    return { draft, keys: [] };
  }
  const business = { ...draft.business };
  const keys: string[] = [];
  for (const key of BUSINESS_TEXT) {
    const value = doc.business[key];
    if (!value || business[key] !== "") continue;
    if (key === "state" && draft.account !== null) continue;
    business[key] = value;
    keys.push(key);
  }
  if (doc.business.entityType && business.entityType === "") {
    business.entityType = doc.business.entityType;
    keys.push("entityType");
  }
  return keys.length > 0
    ? { draft: { ...draft, business }, keys }
    : { draft, keys };
}

const blankAddress = (a: AddressValue) =>
  [a.street, a.addressLine2, a.city, a.state, a.zip, a.county].every(
    (part) => !part?.trim(),
  );

const blankRow = (r: WcClassRow) =>
  !r.code &&
  !r.documentCode &&
  r.fullTime === undefined &&
  r.partTime === undefined &&
  r.payroll === undefined;

/**
 * The Locations step's locations, from the document, once the step has read
 * what PMS holds: only while that is nothing and location 1 is untouched, so a
 * document never replaces a location. The mailing address too, while the
 * insured is still this submission's own (Nav, 2026-10-08). The keys name each
 * address and row the document filled, and `mailing`. Never the building
 * details: Workers' Comp doesn't ask them (BRK-S390), and a value the broker
 * can't see is never saved.
 */
export function prefillLocations(
  draft: WcDraft,
  doc: WcDocument,
): WcPrefilled | null {
  if (draft.savedLocations === null) return null;
  const untouched =
    draft.savedLocations.length === 0 &&
    draft.locations.length === 1 &&
    isBlankLocation(draft.locations[0]);
  if (!untouched) return { draft, keys: [] };

  const keys: string[] = [];
  for (const l of doc.locations) {
    if (!blankAddress(l.address)) keys.push(l.key);
    for (const r of l.rows) if (!blankRow(r)) keys.push(r.key);
  }
  const locations =
    doc.locations.length > 0
      ? doc.locations.map((l) => ({
          ...l,
          hasBuildingDetails: null,
          building: { ...EMPTY_BUILDING },
        }))
      : draft.locations;
  let mailing: WcMailing = draft.mailing;
  if (doc.mailing && insuredEditable(draft.account, draft.submission)) {
    const { mailing: address } = doc;
    const match = locations.find((l) => sameAddress(l.address, address));
    mailing = match
      ? { location: match.key }
      : { location: null, mailing: address };
    keys.push("mailing");
  }
  return keys.length > 0
    ? { draft: { ...draft, locations, mailing }, keys }
    : { draft, keys };
}

/**
 * The questions of one section the document answers, once the questions have
 * loaded: each one the manifest asks there, as a value of its type (an option
 * the field doesn't offer stays unanswered), and only where the sub-step has
 * no answer (BRK-S387: each section is its own sub-step).
 */
export function prefillAnswers(
  draft: WcDraft,
  doc: WcDocument,
  section: FieldSection,
): WcPrefilled | null {
  if (draft.manifest === null) return null;
  const inSection = new Set(
    draft.manifest.fields
      .filter((f) => f.section === section)
      .map((f) => f.key),
  );
  const answers = { ...draft.answers };
  const keys: string[] = [];
  for (const [key, value] of Object.entries(
    answersFrom(draft.manifest, doc.wc),
  )) {
    if (!inSection.has(key)) continue;
    if (value === undefined || draft.answers[key] !== undefined) continue;
    answers[key] = value;
    keys.push(key);
  }
  return keys.length > 0
    ? { draft: { ...draft, answers }, keys }
    : { draft, keys };
}
